'use client';

import type { NavMainProps } from '../types';

import React, { useEffect } from 'react';

import { useTranslate } from '@/core/locales';
import { TRANSLATION_NAMESPACES } from '@/core/constants';

import { Logo } from '@/components/logo';
import { Social } from '@/components/social';
import { Iconify } from '@/components/iconify';
import { Box, Link, Button, Drawer, Divider, IconButton, Typography } from '@/components/ui';

import { SignInButton } from '@/containers/layouts/components/sign-in-button';

import { paths } from '@/routes/paths';
import { usePathname } from '@/routes/hooks';
import { RouterLink } from '@/routes/components';

import { Nav, NavUl } from '../components';
import { NavList } from './nav-mobile-list';

export type NavMobileProps = NavMainProps & {
  open: boolean;
  socialLinksData: any;
  onClose: () => void;
  slots?: {
    topArea?: React.ReactNode;
    bottomArea?: React.ReactNode;
  };
  className?: string;
};

export function NavMobile({
  data,
  socialLinksData,
  open,
  onClose,
  slots,
  className,
}: NavMobileProps) {
  const pathname = usePathname();
  const { t } = useTranslate(TRANSLATION_NAMESPACES.NAVIGATION);
  const { t: tFooter } = useTranslate(TRANSLATION_NAMESPACES.FOOTER);
  // Close menu on route change
  useEffect(() => {
    if (open) {
      onClose();
    }
  }, [pathname]);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      anchor="right"
      className="max-w-[var(--layout-nav-mobile-width)] w-full py-1 overflow-y-auto"
    >
      {slots?.topArea ?? (
        <Box className="px-4">
          <Box className="pt-3 pb-2 pl-2.5 flex justify-between items-center">
            <Logo className="w-[109px] md:w-[109px] h-[43px]" />

            <Box className="flex justify-between items-center">
              <SignInButton isSmall className="px-1" />

              <IconButton onClick={onClose} className="px-1">
                <Iconify icon="lucide:x" width={30} />
              </IconButton>
            </Box>
          </Box>

          <Divider className="mb-10 border-t-2" />
        </Box>
      )}

      {/* Navigation List */}
      <Box className="flex-1 overflow-y-auto">
        <Nav className="pb-3 flex flex-col">
          <NavUl>
            {data?.map((list, index) => (
              <NavList key={index} data={list} className="mb-3" />
            ))}
          </NavUl>
        </Nav>
      </Box>

      {/* Bottom Area */}
      {slots?.bottomArea ?? (
        <Box className="py-3 px-4 pt-[52px] flex flex-col gap-8">
          <Button component={RouterLink} fullWidth variant="contained" href={paths.auth.singIn}>
            {t('actions.tryForFree')}
          </Button>

          <Box className="mx-auto w-full my-4">
            <Social links={socialLinksData} />
          </Box>

          {/* Company Information */}
          <Box className="text-center">
            <Typography
              variant="caption"
              className="text-gray-600 text-sm text-black block mb-[26px]"
            >
              {tFooter('footer.contacts.entrepreneur')}
            </Typography>
            <Typography
              variant="caption"
              className="text-gray-600 text-sm text-black block mb-[26px]"
            >
              {tFooter('footer.contacts.taxId')}
            </Typography>
            <Typography
              variant="caption"
              className="text-gray-600 text-sm text-black block mb-[26px]"
            >
              {tFooter('footer.contacts.businessId')}
            </Typography>
            <Link
              variant="caption"
              className="m-0 p-0 mt-0 font-normal text-left text-sm text-black block mb-[26px]"
              href={t('footer.contacts.emailLink')}
              rel="noopener noreferrer"
            >
              {tFooter('footer.contacts.email')}
            </Link>
          </Box>

          <Box className="pb-[30px] pt-[54px]">
            <Typography variant="caption" className="text-gray-500 text-sm">
              {tFooter('footer.copyright', { year: new Date().getFullYear() })}
            </Typography>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
